import { generateKeyPair, publicKeyOf, isHex, verifyHex } from '../../src/core/crypto.js';
import { signRequest, withdrawBody } from '../../src/core/client.js';
import { blind, noteAuth, noteSecrets, parseG2, unblind, verifyBlindSigs, NOTE_DENOMS, MAX_NOTES_PER_REQUEST } from '../../src/core/notes.js';
import { bodyOf, entryHash } from '../../src/core/log.js';
import { canonicalize } from '../../src/core/canonical.js';
import type { Info } from './api.js';
import type { LogEntry, RequestBody } from '../../src/core/types.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/hashes/utils.js';
import { api, ApiError, type NoteMint } from './api.js';

/**
 * The built-in wallet. One recovery key (the seed) is account 0, which holds
 * deposits and sends withdrawals. Every coin is traded from its own account,
 * derived from the seed, so outsiders see unrelated accounts.
 *
 * ZEC moves between your own accounts through shield notes: account A pays
 * for blind-signed notes, account B redeems them. Nobody, not even the
 * operator, can tell which mint a redemption came from. Note serials and
 * blinding factors come from the seed too, so the recovery key restores them.
 */
const KEY = 'dazzle.wallet.v1';
const RECEIPTS = 'dazzle.receipts.v1.';
const META = 'dazzle.meta.v1.';
const NOTES = 'dazzle.notes.v1.';
// Keys used before the rename; a wallet found there is moved over once.
const OLD_KEY = 'zecpad.wallet.v1';
const OLD_RECEIPTS = 'zecpad.receipts.v1.';

export interface WalletState {
  publicKey: string;
  secretKey: string;
}

export interface Note {
  k: number; // counter the serial and blinding were derived from
  denom: string;
  serial: string;
  sig: string;
  at?: number; // when it was minted (log time, ms)
}

interface Meta {
  coins: Record<string, number>; // token id → account index
  nextIndex: number; // next unused account index (0 is the main account)
  noteK: number; // next unused note counter
  withdrawals: Record<string, { address: string; salt: string }>; // commitment → what only you know
  withdrawK?: number; // next withdrawal salt counter (salts come from the seed, so a restore can still prove a destination)
}

// ------------------------------------------------------------ the log this site runs

/**
 * Which log the site serves, its sequencer key and the server clock offset.
 * The first log seen is remembered (trust on first use): if the site later
 * serves a different one, the wallet says so instead of quietly trusting it.
 */
const PINNED = 'dazzle.pinned.v1';
let site: { chainId: string; sequencer: string; offset: number; noteKeys: Record<string, string> } | null = null;
let siteChanged = false;

export function siteInfoChanged(): boolean {
  return siteChanged;
}

/** Feed the latest /api/info (the store does this on every refresh). */
export function setSiteInfo(info: Info) {
  site = { chainId: info.chainId, sequencer: info.sequencer, offset: (info.now ?? Date.now()) - Date.now(), noteKeys: info.genesis.noteKeys };
  const pinned = load<{ chainId: string } | null>(PINNED, null);
  if (!pinned) store(PINNED, { chainId: info.chainId });
  siteChanged = !!pinned && pinned.chainId !== info.chainId;
}

/** Accept the log the site serves now (after the user has read why it changed). */
export function acceptSiteLog() {
  if (site) store(PINNED, { chainId: site.chainId });
  siteChanged = false;
  emit();
}

async function siteInfo() {
  if (!site) setSiteInfo(await api.info());
  return site!;
}

/** A receipt only counts if the sequencer really signed exactly what we sent. */
function checkReceipt(receipt: LogEntry, sent: unknown, sequencer: string) {
  const ok =
    receipt &&
    entryHash(bodyOf(receipt)) === receipt.hash &&
    verifyHex(receipt.sig, receipt.hash, sequencer) &&
    canonicalize(receipt.data) === canonicalize(sent);
  if (!ok) throw new Error('The server answered with a receipt that is not properly signed. Nothing was saved; please report this.');
}

type Sub = () => void;
const subs = new Set<Sub>();
export const onWalletChange = (fn: Sub) => (subs.add(fn), () => subs.delete(fn));
const emit = () => subs.forEach((f) => f());

const load = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};
const store = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked: kept for this tab only */
  }
};

function migrateOldKeys() {
  try {
    const old = localStorage.getItem(OLD_KEY);
    if (!old || localStorage.getItem(KEY)) return;
    localStorage.setItem(KEY, old);
    const pub = (JSON.parse(old) as WalletState).publicKey;
    const rec = localStorage.getItem(OLD_RECEIPTS + pub);
    if (rec && !localStorage.getItem(RECEIPTS + pub)) localStorage.setItem(RECEIPTS + pub, rec);
  } catch {
    /* storage blocked or old value unreadable */
  }
}

function read(): WalletState | null {
  migrateOldKeys();
  const w = load<WalletState | null>(KEY, null);
  if (!w || !isHex(w.secretKey, 32) || publicKeyOf(w.secretKey) !== w.publicKey) return null;
  return w;
}

let current: WalletState | null = read();
let meta: Meta = blankMeta();
let purse: Note[] = [];
const nonces = new Map<number, number>();
let queue: Promise<unknown> = Promise.resolve();
let lock: Promise<unknown> = Promise.resolve();
loadLocal();

export const wallet = () => current;

function blankMeta(): Meta {
  return { coins: {}, nextIndex: 1, noteK: 0, withdrawals: {} };
}

function loadLocal() {
  meta = current ? { ...blankMeta(), ...load<Partial<Meta>>(META + current.publicKey, {}) } : blankMeta();
  purse = current ? load<Note[]>(NOTES + current.publicKey, []) : [];
}

function saveMeta() {
  if (current) store(META + current.publicKey, meta);
}

function savePurse() {
  if (current) store(NOTES + current.publicKey, purse);
  emit();
}

function save(w: WalletState | null) {
  try {
    if (w) localStorage.setItem(KEY, JSON.stringify(w));
    else localStorage.removeItem(KEY);
  } catch {
    /* storage blocked: wallet lives for this tab only */
  }
  current = w;
  nonces.clear();
  loadLocal();
  emit();
}

// ------------------------------------------------------------ accounts

/** Secret key of account `index`: the seed itself for 0, derived for the rest. */
function accountSecret(index: number): string {
  if (!current) throw new Error('Create or import a wallet first.');
  if (index === 0) return current.secretKey;
  return bytesToHex(hkdf(sha256, hexToBytes(current.secretKey), undefined, utf8ToBytes(`DAZZLE/v1/account/${index}`), 32));
}

export function accountKey(index: number): string {
  return index === 0 ? current!.publicKey : publicKeyOf(accountSecret(index));
}

/** Every account index this wallet has used: main, one per coin, and any in between. */
export function accountIndexes(): number[] {
  return current ? Array.from({ length: meta.nextIndex }, (_, i) => i) : [];
}

/** The account a coin is traded from, if one was reserved already. */
export function coinAccount(tokenId: string): number | undefined {
  return meta.coins[tokenId];
}

/** The account a coin is traded from; a new one is reserved the first time. */
export function accountForCoin(tokenId: string): number {
  const known = meta.coins[tokenId];
  if (known !== undefined) return known;
  const i = meta.nextIndex++;
  meta.coins[tokenId] = i;
  saveMeta();
  return i;
}

/** A fresh account not tied to a coin yet (used for a launch, bound once the coin id is known). */
export function newAccount(): number {
  const i = meta.nextIndex++;
  saveMeta();
  return i;
}

export function bindCoin(tokenId: string, index: number) {
  meta.coins[tokenId] = index;
  saveMeta();
}

export function coinOfAccount(index: number): string | undefined {
  return Object.keys(meta.coins).find((t) => meta.coins[t] === index);
}

// ------------------------------------------------------------ lifecycle

export async function createWallet(): Promise<WalletState> {
  const k = generateKeyPair();
  save({ publicKey: k.publicKey, secretKey: k.secretKey });
  await send({ type: 'register' });
  return current!;
}

export async function importWallet(secret: string): Promise<WalletState> {
  const s = secret.trim().toLowerCase();
  if (!isHex(s, 32)) throw new Error('A recovery key is 64 characters of 0-9 and a-f.');
  save({ publicKey: publicKeyOf(s), secretKey: s });
  const acc = await api.account(current!.publicKey);
  if (!acc.exists) await send({ type: 'register' });
  await recover();
  return current!;
}

export function forgetWallet() {
  save(null);
}

/**
 * Rebuild accounts and notes from the seed and the public log: find used
 * accounts, the coins they hold, and every note they minted that is unspent.
 */
export async function recover() {
  if (!current) return;
  // Accounts: stop after a long run of unused indexes (failed launches and buys can skip some).
  let last = 0;
  for (let i = 1, misses = 0; misses < 50; i++) {
    const acc = await api.account(accountKey(i));
    if (acc.exists) {
      last = i;
      misses = 0;
      for (const t of acc.tokens ?? []) if (meta.coins[t.id] === undefined) meta.coins[t.id] = i;
    } else misses++;
  }
  meta.nextIndex = Math.max(meta.nextIndex, last + 1);
  // Notes: download every mint and spent serial on the platform and find ours locally.
  const [mints, spent] = await Promise.all([api.allMints(), api.allSpent()]);
  const { found, nextK } = scanOwnNotes(mints, 0);
  purse = found.filter((n) => !spent.has(`${n.denom}:${n.serial}`));
  meta.noteK = Math.max(meta.noteK, nextK);
  saveMeta();
  savePurse();
}

/** How many unused counters in a row end the scan. Counters are saved only after a mint succeeds, so gaps are rare and short. */
const NOTE_GAP = 256;

/**
 * Walk this wallet's note counters from `fromK`, matching each counter's
 * blinded point (one per denomination) against the public mints. Stops after
 * NOTE_GAP counters in a row that nobody minted. Returns our notes and the
 * first counter that is safe to use next.
 */
function scanOwnNotes(mints: NoteMint[], fromK: number): { found: Note[]; nextK: number } {
  const index = new Map<string, { denom: string; sig: string; ts: number }>();
  for (const m of mints) m.blinded.forEach((b, j) => index.set(b, { denom: m.denom, sig: m.sigs[j], ts: m.ts }));
  const found: Note[] = [];
  let nextK = fromK;
  for (let k = fromK, misses = 0; misses < NOTE_GAP; k++) {
    let hit = false;
    for (const d of NOTE_DENOMS) {
      const s = noteSecrets(current!.secretKey, k, d);
      const m = index.get(blind(d, s.serial, s.r));
      if (!m) continue;
      found.push({ k, denom: m.denom, serial: s.serial, sig: unblind(m.sig, s.r), at: m.ts });
      hit = true;
    }
    if (hit) {
      nextK = k + 1;
      misses = 0;
    } else misses++;
  }
  return { found, nextK };
}

/** Another device used our next counters: move past everything already minted. */
async function resyncNoteK() {
  const { found, nextK } = scanOwnNotes(await api.allMints(), meta.noteK);
  const spent = await api.allSpent();
  const known = new Set(purse.map((n) => n.serial));
  for (const n of found) if (!known.has(n.serial) && !spent.has(`${n.denom}:${n.serial}`)) purse.push(n);
  meta.noteK = Math.max(meta.noteK, nextK);
  saveMeta();
  savePurse();
}

// ------------------------------------------------------------ signing

async function freshNonce(index: number): Promise<number> {
  const acc = await api.account(accountKey(index));
  return acc.exists ? acc.nonce! : 0;
}

/**
 * Sign with account `index` and submit. Requests are queued so nonces stay
 * in order even when the user clicks fast; a nonce error triggers one resync
 * and retry.
 */
export function send(body: RequestBody, index = 0): Promise<LogEntry> {
  const run = async (): Promise<LogEntry> => {
    if (!current) throw new Error('Create or import a wallet first.');
    const info = await siteInfo();
    if (!nonces.has(index)) nonces.set(index, await freshNonce(index));
    const attempt = async () => {
      const n = nonces.get(index)! + 1;
      // Signed for this log, at the server's clock: requests far from it are refused.
      const sreq = signRequest(accountSecret(index), accountKey(index), n, body, info.chainId, Date.now() + info.offset);
      const receipt = await api.submit(sreq);
      checkReceipt(receipt, sreq, info.sequencer);
      nonces.set(index, n);
      keepReceipt(receipt);
      return receipt;
    };
    try {
      return await attempt();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'bad_nonce') {
        nonces.set(index, await freshNonce(index));
        return attempt();
      }
      if (err instanceof ApiError && (err.code === 'stale' || err.code === 'wrong_chain')) {
        // Our clock or our idea of the log is off: refresh both and sign again once.
        setSiteInfo(await api.info());
        Object.assign(info, site);
        return attempt();
      }
      throw err;
    }
  };
  const p = queue.then(run, run);
  queue = p.catch(() => undefined);
  return p;
}

/** Across browser tabs on the same wallet, one note counter reservation at a time (where the browser supports it). */
function tabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: { request: (name: string, cb: () => Promise<T>) => Promise<T> } }).locks;
  return locks ? locks.request(`dazzle-notes-${current?.publicKey ?? ''}`, fn) : fn();
}

/** Purse operations run one at a time so two of them never spend the same note. */
function exclusive<T>(fn: () => Promise<T>): Promise<T> {
  const p = lock.then(fn, fn);
  lock = p.catch(() => undefined);
  return p;
}

// ------------------------------------------------------------ notes

export const notes = () => purse;
export const notesZat = () => purse.reduce((s, n) => s + BigInt(n.denom), 0n);

const DENOMS_DESC = [...NOTE_DENOMS].map(BigInt).sort((a, b) => (a > b ? -1 : 1));

/** The part of `zat` that whole notes can hold (the rest stays where it is). */
export function noteableZat(zat: bigint): bigint {
  return zat - (zat % DENOMS_DESC[DENOMS_DESC.length - 1]);
}

/** Turn `zat` of account `index` into notes, largest denominations first. */
export function makePrivate(index: number, zat: bigint): Promise<bigint> {
  return exclusive(async () => {
    let left = zat;
    let made = 0n;
    for (const d of DENOMS_DESC) {
      let count = Number(left / d);
      while (count > 0) {
        const n = Math.min(count, MAX_NOTES_PER_REQUEST);
        const denom = d.toString();
        const mintOnce = () =>
          tabLock(async () => {
            // Another tab may have minted meanwhile: start from the highest counter any tab saved.
            meta.noteK = Math.max(meta.noteK, load<Partial<Meta>>(META + current!.publicKey, {}).noteK ?? 0);
            const k0 = meta.noteK;
            const ks = Array.from({ length: n }, (_, i) => k0 + i);
            const secrets = ks.map((k) => noteSecrets(current!.secretKey, k, denom));
            const blinded = secrets.map((s) => blind(denom, s.serial, s.r));
            const e = await send({ type: 'note_mint', denom, blinded }, index);
            const sigs = (e.result as { sigs: string[] }).sigs;
            // Counters are used up only once the mint went through; a failed request leaves no gap.
            meta.noteK = Math.max(meta.noteK, k0 + n);
            saveMeta();
            // The signatures must be good before they become notes (a bad server could hand out worthless ones).
            const pk = parseG2((await siteInfo()).noteKeys[denom]);
            if (!pk || !verifyBlindSigs(pk, blinded, sigs)) throw new Error('The server returned invalid note signatures; please report this.');
            ks.forEach((k, j) => purse.push({ k, denom, serial: secrets[j].serial, sig: unblind(sigs[j], secrets[j].r), at: e.ts }));
            savePurse();
          });
        try {
          await mintOnce();
        } catch (err) {
          // Another device used these counters; nothing was paid. Skip past them and try once more.
          if (!(err instanceof ApiError && err.code === 'dup_blinded')) throw err;
          await resyncNoteK();
          await mintOnce();
        }
        count -= n;
        left -= d * BigInt(n);
        made += d * BigInt(n);
      }
    }
    return made;
  });
}

/**
 * Pick notes worth at least `need`, wasting as little as possible. Among
 * notes of one size the oldest go first: the longer since a note was made,
 * the harder it is to link its redemption to the mint by timing.
 */
function pickNotes(need: bigint): Note[] | null {
  const pool = [...purse].sort((a, b) => (BigInt(b.denom) > BigInt(a.denom) ? 1 : BigInt(b.denom) < BigInt(a.denom) ? -1 : (a.at ?? 0) - (b.at ?? 0)));
  const picked: Note[] = [];
  let left = need;
  for (const n of pool) {
    if (left <= 0n) break;
    if (BigInt(n.denom) <= left) {
      picked.push(n);
      left -= BigInt(n.denom);
    }
  }
  if (left > 0n) {
    // One more note covers the remainder: the smallest that is big enough.
    const rest = pool.filter((n) => !picked.includes(n)).sort((a, b) => (BigInt(a.denom) > BigInt(b.denom) ? 1 : -1));
    const one = rest.find((n) => BigInt(n.denom) >= left) ?? null;
    if (!one) return null;
    picked.push(one);
  }
  return picked;
}

export class NotEnoughPrivate extends Error {}

/** Notes younger than this are flagged: a mint and a redemption minutes apart can be linked by timing. */
export const FRESH_NOTE_MS = 30 * 60_000;

/**
 * What topping up an account to `zat` would use: whether there is enough,
 * and how old the youngest note involved is (Infinity when no notes are needed).
 */
export function planFund(zat: bigint, balance: bigint): { enough: boolean; youngestMs: number } {
  if (balance >= zat) return { enough: true, youngestMs: Infinity };
  const picked = pickNotes(zat - balance);
  if (!picked) return { enough: false, youngestMs: Infinity };
  const newest = Math.max(...picked.map((n) => n.at ?? 0));
  return { enough: true, youngestMs: newest ? Date.now() - newest : Infinity };
}

/** Make sure account `index` holds at least `zat`, redeeming notes into it if needed. */
export function fund(index: number, zat: bigint, balance: bigint): Promise<void> {
  return exclusive(async () => {
    if (balance >= zat) return;
    const picked = pickNotes(zat - balance);
    if (!picked) throw new NotEnoughPrivate('Not enough private ZEC. Make some ZEC private in your wallet first.');
    const byDenom = new Map<string, Note[]>();
    for (const n of picked) byDenom.set(n.denom, [...(byDenom.get(n.denom) ?? []), n]);
    for (const [denom, list] of byDenom) {
      for (let i = 0; i < list.length; i += MAX_NOTES_PER_REQUEST) {
        const chunk = list.slice(i, i + MAX_NOTES_PER_REQUEST);
        try {
          // Each note signs which account it goes to, so nobody who sees this request can redeem it elsewhere.
          const { chainId } = await siteInfo();
          const to = accountKey(index);
          const notesOut = chunk.map(({ k, serial, sig }) => ({ serial, sig, auth: noteAuth(noteSecrets(current!.secretKey, k, denom).secret, chainId, denom, serial, to) }));
          await send({ type: 'note_redeem', denom, notes: notesOut }, index);
        } catch (err) {
          // A note spent elsewhere (another device): drop spent ones and let the user retry.
          if (err instanceof ApiError && err.code === 'double_spend') await dropSpent();
          throw err;
        }
        purse = purse.filter((n) => !chunk.includes(n));
        savePurse();
      }
    }
  });
}

async function dropSpent() {
  if (!purse.length) return;
  const spent = await api.allSpent();
  purse = purse.filter((n) => !spent.has(`${n.denom}:${n.serial}`));
  savePurse();
}

// ------------------------------------------------------------ withdrawals

/** Withdraw from the main account with the destination sealed; the address and salt stay in this browser. */
export async function withdraw(zat: bigint, address: string, encKey: string): Promise<LogEntry> {
  // The salt comes from the seed and a counter, so after a restore the destination can still be proven.
  const k = meta.withdrawK ?? 0;
  const salt = bytesToHex(hkdf(sha256, hexToBytes(current!.secretKey), undefined, utf8ToBytes(`DAZZLE/v3/withdraw-salt/${k}`), 16));
  const w = withdrawBody(encKey, zat.toString(), address, accountKey(0), salt);
  meta.withdrawals[w.commit] = { address, salt: w.salt };
  meta.withdrawK = k + 1;
  saveMeta();
  return send(w.body, 0);
}

export function withdrawalAddress(commit: string): string | undefined {
  return meta.withdrawals[commit]?.address;
}

// ------------------------------------------------------------ receipts

export function receipts(): LogEntry[] {
  if (!current) return [];
  return load<LogEntry[]>(RECEIPTS + current.publicKey, []);
}

function keepReceipt(e: LogEntry) {
  if (!current) return;
  const list = receipts();
  list.push(e);
  store(RECEIPTS + current.publicKey, list.slice(-1000));
  emit();
}

export function downloadReceipts() {
  const blob = new Blob([JSON.stringify({ receipts: receipts(), withdrawals: meta.withdrawals }, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `dazzle-receipts-${current?.publicKey.slice(0, 8)}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
